import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Legend } from 'recharts';

const CurveChartComponent = ({
    data,
    title = "Curve Chart",
    dataKeys = ['value'], // Array of data keys to plot
    colors = ['#facc15', '#3b82f6', '#10b981'], // Colors for each line
    loading = false,
    height = 300,
    showLabels = true,
    smooth = true
}) => {
    if (loading) {
        return (
            <div className="w-full rounded-2xl flex flex-col p-5" style={{
                background: "#2d3347",
                boxShadow: '0 2px 12px #0004',
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#facc15', textAlign: 'center' }}>
                    {title}
                </div>
                <div style={{ color: '#888', textAlign: 'center' }}>
                    جاري تحميل البيانات...
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="w-full rounded-2xl flex flex-col p-5" style={{
                background: "#2d3347",
                boxShadow: '0 2px 12px #0004',
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#facc15', textAlign: 'center' }}>
                    {title}
                </div>
                <div style={{ color: '#888', textAlign: 'center' }}>
                    لا توجد بيانات متاحة
                </div>
            </div>
        );
    }

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'rgba(32, 42, 58, 0.95)',
                    border: '1px solid #444',
                    borderRadius: 8,
                    padding: '12px 16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    color: '#fff',
                    fontSize: 14,
                    backdropFilter: 'blur(10px)',
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#facc15' }}>
                        {label}
                    </p>
                    {payload.map((item, index) => (
                        <p key={index} style={{ margin: '4px 0', color: item.color }}>
                            {item.name}: {Math.round(item.value)}%
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Get dot color based on value
    const getDotColor = (value) => {
        if (value >= 70) return '#6aa84f';
        if (value >= 40) return '#fd7e00';
        return '#f44336';
    };

    return (
        <div className="w-full rounded-2xl flex flex-col p-5" style={{
            background: "#2d3347",
            boxShadow: '0 2px 12px #0004',
            minHeight: height,
        }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#facc15', textAlign: 'center', marginBottom: 16 }}>
                {title}
            </div>

            <ResponsiveContainer width="100%" height={height - 60}>
                <LineChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" opacity={0.3} />

                    <XAxis
                        dataKey="month"
                        stroke="#888"
                        tick={{ fill: '#fff', fontSize: 12 }}
                    />

                    <YAxis
                        stroke="#888"
                        tick={{ fill: '#fff', fontSize: 12 }}
                        domain={[0, 100]}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    {dataKeys.length > 1 && <Legend wrapperStyle={{ color: '#fff' }} />}

                    {dataKeys.map((key, index) => (
                        <Line
                            key={key}
                            type={smooth ? "monotone" : "linear"}
                            dataKey={key}
                            stroke={colors[index % colors.length]}
                            strokeWidth={3}
                            dot={(props) => {
                                const value = props.payload[key];
                                const color = getDotColor(value);
                                return (
                                    <circle
                                        cx={props.cx}
                                        cy={props.cy}
                                        r={5}
                                        fill={color}
                                        stroke="#fff"
                                        strokeWidth={2}
                                        style={{ transition: 'all 0.3s ease' }}
                                    />
                                );
                            }}
                            activeDot={{
                                r: 8,
                                fill: colors[index % colors.length],
                                stroke: '#fff',
                                strokeWidth: 2,
                                style: { transition: 'all 0.3s ease' }
                            }}
                        >
                            {showLabels && (
                                <LabelList
                                    dataKey={key}
                                    position="top"
                                    offset={15}
                                    fill={colors[index % colors.length]}
                                    fontSize={11}
                                    fontWeight="bold"
                                    formatter={(value) => `${Math.round(value)}%`}
                                />
                            )}
                        </Line>
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default CurveChartComponent;
